import { useState } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Avatar } from '@components/avatar';
import { Badge } from '@components/badge';
import { Card } from '@components/card';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconEdit } from '@components/icons/edit';
import { IconHistory } from '@components/icons/history';
import { IconCompare } from '@components/icons/icon-compare';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconReplace } from '@components/icons/replace';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Shapes } from '@components/shapes';
import { hideLeftSidebar, InfoButton, Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';

import { useJourney, usePublishedVersions, useUnpublishedChanges } from 'api/journeys/hooks';
import { PublishedVersion } from 'api/types';

import { RestoreVersionContent } from './restore-version';
import { VersionComparisonContent } from './version-comparison';

export type VersionHistorySidebarProps = {
  journeyShortId: string;
  hasUnpublishedChanges: boolean;
  basedOnVersionId?: string | null;
  previewVersion?: PublishedVersion | null;
  onSelectVersion?: (version: PublishedVersion | null) => void;
  onRenameVersion?: (version: PublishedVersion) => void;
  onClose?: () => void;
  onRestoreSuccess?: () => void;
  initialView?: VersionPanelView;
  initialVersionForAction?: SelectedVersionForAction;
};

export const useVersionHistoryPanelSidebar = () =>
  useSidebar(VersionHistoryPanelSidebar, {
    variant: 'REGULAR',
    hasBackdrop: false,
    position: 'left',
  });

const VersionHistoryPanelSidebar = (props: VersionHistorySidebarProps) => {
  const { t } = useScopedTranslate(locale);
  const {
    journeyShortId,
    hasUnpublishedChanges,
    basedOnVersionId,
    previewVersion,
    onSelectVersion,
    onRenameVersion,
    onClose,
    onRestoreSuccess,
    initialView = 'list',
    initialVersionForAction,
  } = props;

  const [view, setView] = useState<VersionPanelView>(initialView);
  const [selectedVersionForAction, setSelectedVersionForAction] =
    useState<SelectedVersionForAction | null>(initialVersionForAction ?? null);

  const [selectedPreviewVersion, setSelectedPreviewVersion] = useState<PublishedVersion | null>(
    previewVersion ?? null,
  );

  const getTitle = () => {
    switch (view) {
      case 'compare':
        return t('compareVersion');
      case 'restore':
        return t('restoreVersion');
      default:
        return t('versionHistory');
    }
  };

  const handleSelectVersion = (version: PublishedVersion | null) => {
    setSelectedPreviewVersion(version);
    onSelectVersion?.(version);
  };

  const handleCompare = (version: PublishedVersion) => {
    setView('compare');
    setSelectedVersionForAction({
      versionId: version.shortId,
      versionNumber: version.versionNumber,
      dateCreated: version.dateCreated,
    });
  };

  const handleRestore = (version: PublishedVersion) => {
    setView('restore');
    setSelectedVersionForAction({
      versionId: version.shortId,
      versionNumber: version.versionNumber,
      dateCreated: version.dateCreated,
    });
  };

  const handleBackToList = () => {
    setView('list');
    setSelectedVersionForAction(null);
  };

  const handleRestoreSuccess = () => {
    onRestoreSuccess?.();
    hideLeftSidebar();
  };

  const handleClose = () => {
    onClose?.();
    hideLeftSidebar();
  };

  const customIcon =
    view === 'list' ? (
      <IconHistory color="elementBlue100" />
    ) : view === 'restore' ? (
      <>
        <IconButton icon={<IconArrowLeft />} variant="ghost-purple" onClick={handleBackToList} />
        <IconReplace color="elementBlue100" />
      </>
    ) : (
      <>
        <IconButton icon={<IconArrowLeft />} variant="ghost-purple" onClick={handleBackToList} />
        <IconCompare color="elementBlue100" />
      </>
    );

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={getTitle()} customIcon={customIcon} onClose={handleClose}>
        {view === 'list' && (
          <InfoButton>
            <VersionHistoryInfo />
          </InfoButton>
        )}
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        {view === 'list' && (
          <VersionHistoryContent
            journeyShortId={journeyShortId}
            hasUnpublishedChanges={hasUnpublishedChanges}
            basedOnVersionId={basedOnVersionId}
            previewVersion={selectedPreviewVersion}
            onSelectVersion={handleSelectVersion}
            onRenameVersion={onRenameVersion}
            onCompare={handleCompare}
            onRestore={handleRestore}
          />
        )}
        {view === 'compare' && selectedVersionForAction && (
          <VersionComparisonContent
            journeyShortId={journeyShortId}
            versionId={selectedVersionForAction.versionId}
            versionNumber={selectedVersionForAction.versionNumber}
            hasUnpublishedChanges={hasUnpublishedChanges}
            onBack={handleBackToList}
          />
        )}
        {view === 'restore' && selectedVersionForAction && (
          <RestoreVersionContent
            journeyShortId={journeyShortId}
            versionId={selectedVersionForAction.versionId}
            hasUnpublishedChanges={hasUnpublishedChanges}
            onBack={handleBackToList}
            onSuccess={handleRestoreSuccess}
          />
        )}
      </Sidebar.Content>
    </Sidebar>
  );
};

const VersionHistoryInfo = () => {
  const { t, styledTranslate } = useScopedTranslate(locale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('versionHistoryInfoTitle')} />
      <Text font="regular14" text={t('versionHistoryDescription')} />
      <ul>
        <li>
          <Text font="regular14" text={t('versionHistoryPreviewAction')} />
        </li>
        <li>
          <Text
            font="regular14"
            text={styledTranslate('versionHistoryCompareAction', {
              header: <Text font="bold14" text={t('versionHistoryCompareActionHeader')} />,
            })}
          />
        </li>
        <li>
          <Text
            font="regular14"
            text={styledTranslate('versionHistoryRestoreAction', {
              header: <Text font="bold14" text={t('versionHistoryRestoreActionHeader')} />,
            })}
          />
        </li>
      </ul>
      <Text
        font="regular14"
        text={styledTranslate('versionHistoryMoreDetails', {
          helpSection: (
            <ActionText.ExternalLink
              font="regular14"
              href={'https://help.journeys.50skills.app/en/articles/560569-versions'}
            >
              {t('versionHistoryMoreDetailsHelpSection')}
            </ActionText.ExternalLink>
          ),
        })}
      />
    </Stack>
  );
};

const VersionCard = ({
  version,
  isLatest,
  isSelected,
  isLast,
  hasUnpublishedChanges,
  onSelect,
  onRestore,
  onCompare,
  onRename,
}: {
  version: PublishedVersion;
  isLatest: boolean;
  isSelected: boolean;
  isLast: boolean;
  hasUnpublishedChanges: boolean;
  onSelect: () => void;
  onRestore: () => void;
  onCompare: () => void;
  onRename: () => void;
}) => {
  const { t } = useScopedTranslate(locale);

  const canRestoreOrCompare = !isLatest || hasUnpublishedChanges;
  const versionLabel = version.name ?? t('versionNumber', { version: version.versionNumber });

  return (
    <Stack orientation="horizontal" space={3}>
      <TimelineColumn>
        {isLatest ? <TimelineSpacer /> : <TimelineLineTop />}
        <Shapes.Circle size={8} color={isSelected ? 'elementBlue100' : 'border20'} />
        {isLast ? <TimelineSpacer /> : <TimelineLineBottom />}
      </TimelineColumn>
      <Card
        spaceX={4}
        spaceY={3}
        interactive
        onClick={onSelect}
        isActive={isSelected}
        hasBorder
        style={{ flex: 1 }}
      >
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" space={3} align="center">
            <Avatar
              src={version.createdBy?.picture}
              name={version.createdBy?.name ?? t('core.user')}
              color="purple"
            />
            <div>
              <Text font="semibold14" text={versionLabel} color="textPrimary" />
              <Text
                font="regular14"
                color="textSecondary"
                text={abbreviateDate(version.dateCreated, 'en', 'datetime')}
              />
              <Text
                color="textHelper"
                font="regular14"
                text={t(version.actionCount === 1 ? 'actionCount' : 'actionCountPlural', {
                  count: version.actionCount,
                })}
              />
            </div>
          </Stack>
          <Stack orientation="horizontal" space={2} align="center">
            {isLatest && <Badge variant="green" text={t('core.active')} />}
            <Menu
              buttonProps={{
                variant: 'ghost',
                iconRight: <IconMoreVertical />,
                stopPropagation: true,
              }}
            >
              {canRestoreOrCompare && (
                <>
                  <Menu.Item
                    label={t('restoreVersion')}
                    icon={<IconReplace />}
                    onSelect={onRestore}
                  />
                  <Menu.Item
                    label={t('compareVersions')}
                    icon={<IconCompare />}
                    onSelect={onCompare}
                  />
                </>
              )}
              <Menu.Item label={t('renameVersion')} icon={<IconEdit />} onSelect={onRename} />
            </Menu>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
};

const CurrentVersionCard = ({
  hasUnpublishedChanges,
  basedOnVersionNumber,
  unpublishedChangesCount,
  isSelected,
  onSelect,
}: {
  hasUnpublishedChanges: boolean;
  basedOnVersionNumber?: number | null;
  unpublishedChangesCount?: number;
  isSelected: boolean;
  onSelect: () => void;
}) => {
  const { t } = useScopedTranslate(locale);

  if (!hasUnpublishedChanges) {
    return null;
  }

  const title = basedOnVersionNumber
    ? t('draftBasedOn', { version: `Version ${basedOnVersionNumber}` })
    : t('currentVersion');

  return (
    <Card space={4} interactive onClick={onSelect} isActive={isSelected}>
      <Stack orientation="horizontal" space={3} align="flex-start">
        <Avatar name="Current" size="md" />
        <div>
          <Text font="semibold14" text={title} />
          {unpublishedChangesCount !== undefined && (
            <Text
              color="textHelper"
              font="regular14"
              text={t(unpublishedChangesCount === 1 ? 'updatesCount' : 'updatesCountPlural', {
                count: unpublishedChangesCount,
              })}
            />
          )}
          <DraftIndicator>
            <Shapes.Circle size={8} color="elementIndigo100" />
            <Text font="semibold12" color="elementIndigo100" text={t('draft')} />
          </DraftIndicator>
        </div>
      </Stack>
    </Card>
  );
};

const TimelineColumn = styled(Stack).attrs({
  space: 2,
  align: 'center',
})({
  width: pxToRem(8),
  flexShrink: 0,
});

// Half of Stack space={2} gap (8px / 2 = 4px) to make lines meet in the middle
const timelineLineExtension = pxToRem(-4);
const timelineLineCommonStyle = {
  flex: 1,
  width: pxToRem(2),
  backgroundColor: theme.color.border10,
};

const TimelineLineTop = styled.div({
  ...timelineLineCommonStyle,
  marginTop: timelineLineExtension,
});

const TimelineLineBottom = styled.div({
  ...timelineLineCommonStyle,
  marginBottom: timelineLineExtension,
});

const TimelineSpacer = styled.div({
  flex: 1,
});

const DraftIndicator = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(4),
});

export type VersionHistoryProps = {
  journeyShortId: string;
  hasUnpublishedChanges: boolean;
  basedOnVersionId?: string | null;
  previewVersion?: PublishedVersion | null;
  onSelectVersion?: (version: PublishedVersion | null) => void;
  onRenameVersion?: (version: PublishedVersion) => void;
  onClose?: () => void;
};

export type VersionHistoryContentProps = Omit<VersionHistoryProps, 'onClose'> & {
  onCompare?: (version: PublishedVersion) => void;
  onRestore?: (version: PublishedVersion) => void;
};

export const VersionHistoryContent = ({
  journeyShortId,
  hasUnpublishedChanges,
  basedOnVersionId,
  previewVersion,
  onSelectVersion,
  onRenameVersion,
  onCompare,
  onRestore,
}: VersionHistoryContentProps) => {
  const { t } = useScopedTranslate(locale);

  const { data: versions, isLoading, error } = usePublishedVersions(journeyShortId);
  const { data: unpublishedChanges } = useUnpublishedChanges(journeyShortId);
  const { data: journey } = useJourney(journeyShortId);

  const liveHasUnpublishedChanges = journey?.hasUnpublishedChanges ?? hasUnpublishedChanges;

  const effectiveHasUnpublishedChanges =
    liveHasUnpublishedChanges ||
    (unpublishedChanges?.summary !== undefined &&
      unpublishedChanges.summary.added +
        unpublishedChanges.summary.removed +
        unpublishedChanges.summary.modified >
        0);

  if (isLoading) {
    return <Text text={t('loadingVersions')} />;
  }

  if (error) {
    return (
      <Card space={4} variant="red">
        <Text color="elementRed100" text={t('errorLoadingVersions')} />
      </Card>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <Card space={4} variant="blue">
        <Text text={t('noVersions')} />
      </Card>
    );
  }

  const basedOnVersion = basedOnVersionId
    ? versions.find((v) => v.shortId === basedOnVersionId)
    : null;

  const unpublishedChangesCount = unpublishedChanges?.summary
    ? unpublishedChanges.summary.added +
      unpublishedChanges.summary.removed +
      unpublishedChanges.summary.modified
    : undefined;

  const handleSelectDraft = () => {
    // Pass null to clear the preview and return to current draft
    (onSelectVersion as (version: PublishedVersion | null) => void)?.(null);
  };

  // When no unpublished changes, the newest version should act as "selected"
  // (visually highlighted) but remain in normal editing mode (not preview mode)
  const newestVersionActsAsDraft = !effectiveHasUnpublishedChanges && versions.length > 0;
  const newestVersionShortId = versions[0]?.shortId;

  // Draft card is selected only when no preview AND there are unpublished changes
  const isDraftSelected = previewVersion === null && effectiveHasUnpublishedChanges;

  return (
    <Stack space={4}>
      <CurrentVersionCard
        hasUnpublishedChanges={effectiveHasUnpublishedChanges}
        basedOnVersionNumber={basedOnVersion?.versionNumber}
        unpublishedChangesCount={unpublishedChangesCount}
        isSelected={isDraftSelected}
        onSelect={handleSelectDraft}
      />
      {effectiveHasUnpublishedChanges && <Separator />}
      <Stack space={2}>
        {versions.map((version, index) => {
          const isNewest = index === 0;
          // Newest version is selected as default when it acts as draft and no preview is active
          const isSelectedAsDefault =
            isNewest && newestVersionActsAsDraft && previewVersion === null;

          return (
            <VersionCard
              key={version.shortId}
              version={version}
              isLatest={isNewest}
              isLast={index === versions.length - 1}
              isSelected={previewVersion?.shortId === version.shortId || isSelectedAsDefault}
              hasUnpublishedChanges={effectiveHasUnpublishedChanges}
              onSelect={() => {
                // If this is the newest version acting as draft, return to normal editing mode
                if (version.shortId === newestVersionShortId && newestVersionActsAsDraft) {
                  handleSelectDraft(); // Clear preview to return to normal editing
                  return;
                }
                return onSelectVersion?.(version);
              }}
              onRestore={() => {
                return onRestore?.(version);
              }}
              onCompare={() => {
                return onCompare?.(version);
              }}
              onRename={() => {
                return onRenameVersion?.(version);
              }}
            />
          );
        })}
      </Stack>
    </Stack>
  );
};

export type VersionPanelView = 'list' | 'compare' | 'restore';

export type SelectedVersionForAction = {
  versionId: string;
  versionNumber: number;
  dateCreated: string;
};

export const locale = {
  en: {
    versionHistory: 'Version history',
    versionHistoryInfoTitle: 'About versions',
    versionHistoryDescription:
      'View and manage published versions of this journey,\na version is created each time a journey is published',
    versionHistoryPreviewAction: 'Click a version to preview the version in the builder',
    versionHistoryCompareActionHeader: 'Options → Compare versions',
    versionHistoryCompareAction:
      '{header}To see the changes made between the selected version and your current draft',
    versionHistoryRestoreActionHeader: 'Options → Restore version',
    versionHistoryRestoreAction: '{header}To revert the Journey to the selected version',
    versionHistoryMoreDetails:
      'Need even more details? Check out the explanation in our {helpSection}',
    versionHistoryMoreDetailsHelpSection: 'help section',
    versionNumber: 'Version {version}',
    latest: 'Latest',
    actionCount: '{count} Action',
    actionCountPlural: '{count} Actions',
    preview: 'Preview',
    compareVersions: 'Compare versions',
    restore: 'Restore',
    renameVersion: 'Edit version name',
    loadingVersions: 'Loading versions...',
    noVersions: 'No published versions yet. Publish your journey to create the first version.',
    publishedVersions: 'Published versions ({count})',
    errorLoadingVersions: 'Error loading versions. Please try again.',
    draftBasedOn: 'Draft based on {version}',
    currentVersion: 'Current version',
    updatesCount: '{count} Update',
    updatesCountPlural: '{count} Updates',
    draft: 'Draft',
    compareWithCurrent: 'Compare version {version}',
    compareVersion: 'Compare version',
    restoreVersion: 'Restore version',
  },
};
